/**
 * POS sell-screen sounds — OPERATOR surface only.
 *
 * A tiny named-cue module modeled on the kiosk's ``admin-extras/sound.js``:
 * prime-on-first-gesture (unlocks browser autoplay), a single ``play(name)``
 * entry, and an extensible cue catalog. Transactional cues are fired by callers
 * on the CONFIRMED (server-acked) outcome — never optimistically on tap.
 *
 * A cue maps to a single file OR a POOL (one is picked at random each play) and
 * an optional volume. Files live in ``web/public/pos-sounds/`` (from the crew's
 * examples/pos_soundsiv kit, by event folder). A missing file degrades to
 * silence. The customer-facing display stays SILENT — do not import this there.
 */

export type PosCue =
  | "scanOk" // a barcode resolved to an article (played quiet)
  | "scanErr" // unknown / unresolved barcode
  | "cashPayment" // a payment is settled — "payment initiated" pool (random)
  | "cardOk" // a card reached `paid` — the original strichliste card sound
  | "cardErr" // a card declined / expired
  | "storno" // a line/order reversed — "cancel order position" pool (random)
  | "deposit" // (unused — cup/Bottle quick-actions are silent)
  | "hidden" // the crew-logo easter-egg button (ohhh)
  | "tip"; // the tip button — "tips" pool (random)

type CueDef = { src: string | string[]; volume?: number };

// Numbered pool: /pos-sounds/<dir>/01.mp3 … NN.mp3 (one picked at random).
const pool = (dir: string, n: number): string[] =>
  Array.from({ length: n }, (_, i) => `/pos-sounds/${dir}/${String(i + 1).padStart(2, "0")}.mp3`);

const CUES: Record<PosCue, CueDef> = {
  scanOk: { src: "/pos-sounds/scan-ok.wav", volume: 0.5 },
  scanErr: { src: "/pos-sounds/scan-err.mp3" },
  cashPayment: { src: pool("payment", 29) },
  cardOk: { src: "/pos-sounds/card-ok.mp3" },
  cardErr: { src: "/pos-sounds/card-err.mp3" },
  storno: { src: pool("storno", 2) },
  deposit: { src: "/pos-sounds/deposit.wav" },
  hidden: { src: "/pos-sounds/hidden.wav" },
  tip: { src: pool("tips", 4) },
};

// One reusable Audio element per FILE (so a random pool pick + overlap work).
const elements = new Map<string, HTMLAudioElement>();
let primed = false;

const firstSrc = (cue: PosCue): string => {
  const s = CUES[cue].src;
  return Array.isArray(s) ? s[0] : s;
};
const pickSrc = (cue: PosCue): string => {
  const s = CUES[cue].src;
  return Array.isArray(s) ? s[Math.floor(Math.random() * s.length)] : s;
};

function el(src: string): HTMLAudioElement | null {
  if (typeof Audio === "undefined") return null;
  let a = elements.get(src);
  if (!a) {
    a = new Audio(src);
    a.preload = "auto";
    elements.set(src, a);
  }
  return a;
}

export const posSound = {
  /** Play a cue (a random file from its pool). Safe from async handlers. */
  play(cue: PosCue): void {
    const a = el(pickSrc(cue));
    if (!a) return;
    try {
      a.currentTime = 0;
      a.volume = CUES[cue].volume ?? 1;
      void a.play().catch(() => undefined);
    } catch {
      /* autoplay-blocked / unsupported — ignore */
    }
  },

  /** Unlock autoplay by priming one file per cue muted. Auto-runs on gesture. */
  primeAll(): void {
    if (primed) return;
    primed = true;
    (Object.keys(CUES) as PosCue[]).forEach((cue) => {
      const a = el(firstSrc(cue));
      if (!a) return;
      const v = a.volume;
      try {
        a.volume = 0;
        void a.play().then(
          () => {
            a.pause();
            a.currentTime = 0;
            a.volume = v;
          },
          () => {
            a.volume = v;
          },
        );
      } catch {
        a.volume = v;
      }
    });
  },
};

// Prime on the first user gesture (tablets fire touchstart; desktops/scanners
// fire click) so a later cue from an async settle handler isn't autoplay-blocked.
if (typeof document !== "undefined") {
  const prime = () => {
    posSound.primeAll();
    document.removeEventListener("click", prime, true);
    document.removeEventListener("touchstart", prime, true);
  };
  document.addEventListener("click", prime, true);
  document.addEventListener("touchstart", prime, true);
}
